import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { APP_VERSION, CHANGELOG, type ReleaseNote } from '../changelog';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';

const SEEN_KEY = 'car-evidence-seen-version';

function readSeenVersion() {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

function markSeen() {
  try {
    localStorage.setItem(SEEN_KEY, APP_VERSION);
  } catch {
    // Without storage the window simply shows again next time
  }
}

function Changes({ release }: { release: ReleaseNote }) {
  return (
    <ul className="space-y-3">
      {release.changes.map((change) => (
        <li key={change.title} className="flex gap-3">
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
          <div>
            <p className="font-medium text-secondary-900">{change.title}</p>
            <p className="text-sm text-secondary-600">{change.description}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Version label for the corner of the login page. It opens the "Čo je nové" window, which also opens
 * by itself once for every new version. The window is Slovak only.
 */
export function VersionWithWhatsNew({ className }: { className?: string }) {
  const [open, setOpen] = useState(() => readSeenVersion() !== APP_VERSION);
  const [latest, ...older] = CHANGELOG;

  const close = () => {
    markSeen();
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`text-xs text-secondary-500 hover:text-primary-700 hover:underline ${className ?? ''}`}
        title="Čo je nové"
      >
        verzia {APP_VERSION}
      </button>
      <Modal
        isOpen={open}
        onClose={close}
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            Čo je nové vo verzii {latest.version}
          </span>
        }
        description={`Aktualizácia z ${latest.date}`}
        footer={<Button onClick={close}>Rozumiem</Button>}
      >
        <Changes release={latest} />
        {older.length > 0 && (
          <details className="mt-5 border-t border-secondary-100 pt-4">
            <summary className="cursor-pointer text-sm font-medium text-secondary-700 hover:text-primary-700">
              Staršie zmeny
            </summary>
            {older.map((release) => (
              <div key={release.version} className="mt-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  Verzia {release.version} · {release.date}
                </p>
                <Changes release={release} />
              </div>
            ))}
          </details>
        )}
      </Modal>
    </>
  );
}
